import { useState } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { useProductReadContext } from '@context/ProductContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TestAnswerOutcomeStatus, TestAnswersDto } from '@model/ClientTestAnswerDto'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { DocumentCategoryOptionType } from '@model/DocumentCategoryOptionType'
import { DocumentPropertyType } from '@model/DocumentPropertyType'
import { AppropriatenessTestTakeTestFormValues } from '@model/Test'

import { useLogout } from '@hooks/useLogout'

export const useProductAppTestMutate = () => {
  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { product } = useProductReadContext()
  const navigate = useNavigate()
  const { logout } = useLogout()

  const [isLoading, setLoading] = useState(false)
  const [isSuccess, setSuccess] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [accountApTestAnswers, setAccountApTestAnswers] = useState<TestAnswersDto | undefined>()

  const mutate = async (
    values: AppropriatenessTestTakeTestFormValues,
    setAlreadyUploadedModal: (visible: boolean) => void
  ) => {
    try {
      setLoading(true)

      const selectedAnswers = Object.entries(values.selectedAnswers).map(([, value]) => value)
      const freeAnswers = Object.entries(values.freeAnswers).map(([key, value]) => {
        return { testQuestionId: key, answer: value }
      })

      await apiClient.createClientTests({
        clientId: values.clientId,
        category: values?.category || '',
        testId: values?.testId || '',
        dateTaken: values?.dateTaken || '',
        selectedAnswers,
        freeAnswers,
      })

      const accountApTestAnswers = await apiClient.getAccountAppTestAnswers(locale, product)
      if (accountApTestAnswers.isClientVulnerable) {
        await logout()
        return navigate('/blocked')
      }
      const files = Object.entries(values.documents || {})
        .map(([questionId, filesData]) => filesData.map((x) => ({ ...x, questionId })).flat())
        .flat()
        .filter((x) => !!x.fileName)

      if (files.length > 0) {
        try {
          await apiClient.uploadDocuments({
            documents: files.map((file) => ({
              categoryId: DocumentCategoryType.AppropriatenessTest,
              typeId: DocumentCategoryOptionType.TradingStatement,
              file: file.base64Content,
              filename: file.fileName,
              properties: {
                [DocumentPropertyType.ClientTestId]: accountApTestAnswers.id,
                [DocumentPropertyType.TestQuestionId]: file.questionId,
              },
            })),
          })
        } catch {
          setAlreadyUploadedModal(true)
        }
      }

      if (accountApTestAnswers.outcome.outcome === TestAnswerOutcomeStatus.FAILED) {
        setIsFailed(true)
      }

      if (accountApTestAnswers.outcome.outcome !== TestAnswerOutcomeStatus.FAILED) {
        setSuccess(true)
      }

      setAccountApTestAnswers(accountApTestAnswers)
    } catch (error: unknown) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return { mutate, isLoading, isSuccess, isFailed, accountApTestAnswers }
}
